import i18next, {t} from "i18next";
import {formatTimeUnit, isEndTimeAfterStartTime} from "../../../../../../../../Apis/functions";
import {END_TIME, START_TIME} from "../../../../../Employees/Model/Models";
import {PercentSvg} from "../../../../../../../../components/buttons/IconButtons";
/**
 * @Email : kpozos@ibl.mx
 * @Author: Karen Yunuen Pozos Arias
 * @jobTittle: Full Stack Developer
 *
 */
export const ALL_TIME_NAME = "allTime";
// "All time" covers the whole day and is stored as 00:00 to 00:00.
export const ALL_TIME_START = START_TIME;
export const ALL_TIME_END = START_TIME;
export const PAYROLL_POLICY_GLOBAL = "GLOBAL";

export const BONUS_TYPE_ENUM = {
    PUNCTUALITY: { key: 'PUNCTUALITY', trans: 'punctuality', prefixText:"%", Icon: PercentSvg },
    ATTENDANCE:  { key: 'ATTENDANCE',  trans: 'attendance', prefixText:"%", Icon:PercentSvg },
    values: () => [BONUS_TYPE_ENUM.PUNCTUALITY, BONUS_TYPE_ENUM.ATTENDANCE],
};

export const CONFLICT_RESOLUTION_MODEL = {
    FIRST_MATCH: 'FIRST_MATCH',
    STACKABLE: 'STACKABLE',
    BEST_BENEFIT: 'BEST_BENEFIT'
};

export const CONFLICT_RESOLUTION_LIST = () => [
    {
        id:"FIRST_MATCH",
        name:i18next.t("first_match")
    },
    {
        id:"STACKABLE",
        name:i18next.t("stackable")
    },
    {
        id:"BEST_BENEFIT",
        name:i18next.t("best_benefit")
    }
];

export const getConflictResolutionById = (id) =>
    CONFLICT_RESOLUTION_LIST().find(item => item.id === id) ?? null;

export const STACK_METHOD_MODEL = {
    ADDITIVE: "ADDITIVE",
    COMPOUND: "COMPOUND"
};

export const STACK_METHOD_LIST = () => [
    {
        id:"ADDITIVE",
        name:i18next.t("additive")
    },
    {
        id:"COMPOUND",
        name:i18next.t("compound")
    }
];

export const getStackMethodById = (id) =>
    STACK_METHOD_LIST().find(item => item.id === id) ?? null;

/**
 * Returns the list of available calculation types with display labels and prefix symbols.
 * Called as a function so translations are evaluated at render time.
 * @returns {Array<{id: string, name: string, prefixText: string}>} Calculation type options.
 */
export const CALCULATION_TYPE_MODEL_NAME = {
    MULTIPLIER: 'MULTIPLIER',
    FIXED_AMOUNT: 'FIXED_AMOUNT',
    PERCENTAGE: 'PERCENTAGE'
}
export const CALCULATION_TYPE_MODEL = () => {
    return [
        { id: CALCULATION_TYPE_MODEL_NAME.MULTIPLIER, name: t('multiplier'), prefixText: `x`, prefixTextOperation: `x`},
        { id: CALCULATION_TYPE_MODEL_NAME.FIXED_AMOUNT, name: t('fixed_value'), prefixText: `$`, prefixTextOperation: `+ $`},
        { id: CALCULATION_TYPE_MODEL_NAME.PERCENTAGE, name: t('percentage'), prefixText: `%`, prefixTextOperation: `+ %`},
    ];
};

/**
 * Finds a calculation type entry by its identifier.
 * @param {string} id - Calculation type key (e.g. 'MULTIPLIER').
 * @returns {{id: string, name: string, prefixText: string}|undefined} Matching entry, or undefined.
 */
export const getCalculationTypeById = (id) => {
    return CALCULATION_TYPE_MODEL().find(
        (item) => item.id === id
    );
};

const buildHourOvertimeFormula = ({ type, calculationValue, totalOvertimeMinutes, effectiveRate }) => ({
    effectiveRateTitle: `${t('hourly_rate')} ${type?.prefixTextOperation}${calculationValue}`,
    operationFormula: `${totalOvertimeMinutes} x ${effectiveRate}`
});
// Additive breakdown formulas: each entry is paid over the hourly rate.
const buildOvertimeFormula = ({ type, calculationValue, totalOvertimeMinutes, effectiveRate, rateDelta }) => ({
    rateDeltaTitle: type?.id === CALCULATION_TYPE_MODEL_NAME.MULTIPLIER ?
        `(${t('hourly_rate')} ${type?.prefixTextOperation}${calculationValue}) - ${t('hourly_rate')}`
        : `${type?.prefixTextOperation}${calculationValue}`,
    operationFormula:  `${totalOvertimeMinutes} x ${rateDelta}`
});

// Compound breakdown formulas: each entry stacks over the previous rate.
const buildOvertimeFormulaCompound = ({ type, calculationValue, totalOvertimeMinutes, rateDelta }) => ({
    effectiveRateTitle: `${t('previous_rate')} ${type?.prefixTextOperation} ${calculationValue}`,
    rateDeltaTitle: `${t('effective_rate')} - ${t('previous_rate')}`,
    operationFormula: `${rateDelta} x ${totalOvertimeMinutes}`
});

export const POLICY_CONDITION_MODEL = {
    WEEKLY_RECURRENCE: {
        key: 'WEEKLY_RECURRENCE',
        trans: 'weekly_recurrence',
        legend: (totalOvertime) => {
            return `${formatTimeUnit(totalOvertime ?? 0)} ${t("covered_s").toLowerCase()}`;
        },
        getHourFormula: buildHourOvertimeFormula,
        getFormula: buildOvertimeFormula,
        getFormulaCompound: buildOvertimeFormulaCompound
    },
    DATE_RANGE:        {
        key: 'DATE_RANGE',
        trans: 'date_range',
        legend: (totalOvertime) => {
            return `${formatTimeUnit(totalOvertime ?? 0)} ${t("covered_s").toLowerCase()}`;
        },
        getHourFormula: buildHourOvertimeFormula,
        getFormula: buildOvertimeFormula,
        getFormulaCompound: buildOvertimeFormulaCompound
    },
    HOUR:              {
        key: 'HOUR',
        trans: 'extra_hours',
        legend: (totalOvertime) => {
            return `${formatTimeUnit(totalOvertime ?? 0)} ${t("covered_s").toLowerCase()}`;
        },
        getHourFormula: buildHourOvertimeFormula,
        getFormula: buildOvertimeFormula,
        getFormulaCompound: buildOvertimeFormulaCompound
    },
    BONUS:             {
        key: 'BONUS',
        trans: 'bonus',
        legend: (totalOvertime, calculationType, value) => {
            const type = getCalculationTypeById(calculationType);
            return calculationType === CALCULATION_TYPE_MODEL_NAME.FIXED_AMOUNT
                ? `${type.prefixTextOperation}${value}`
                : `${type.prefixText}${value} (${t("base_salary")})`;
        },
        getHourFormula: buildHourOvertimeFormula,
        getFormula: buildOvertimeFormula,
        getFormulaCompound: buildOvertimeFormulaCompound
    },
    values: () => [
        POLICY_CONDITION_MODEL.WEEKLY_RECURRENCE,
        POLICY_CONDITION_MODEL.DATE_RANGE,
        POLICY_CONDITION_MODEL.HOUR,
        POLICY_CONDITION_MODEL.BONUS,
    ],
};

export const RULE_MODEL = {
    id: null,
    name: '',
    priority: null,
    condition: null,
    calculationType : null,
    calculationValue: '',
    dayArray: [],
    allTime: false,
    startTime: null,
    endTime: null,
    startDate: null,
    endDate: null,
    fromHours: '',
    toHours: '',
    minValue: '',
    maxValue: '',
    bonusType: null,
    value: '',
    statusId: null,
    /**
     * Validates that all required fields for the rule are filled based on its condition type.
     * For by-department policies (isGlobal=false), the rule name is also required.
     * @param {boolean} isGlobal - Whether the parent policy is global.
     * @returns {boolean} True when the rule is fully valid.
     */
    isValid: function (isGlobal) {
        const isNameValid = isGlobal ? true : this.name !== '';
        const isCalculationFieldsValid = this.calculationType !== null && this.calculationValue !== '';

        if (this.condition === POLICY_CONDITION_MODEL.WEEKLY_RECURRENCE.key) {
            return isNameValid && isCalculationFieldsValid &&
                this.dayArray.length > 0 && (this.allTime || (this.startTime !== null && this.endTime !== null) );
        }
        if (this.condition === POLICY_CONDITION_MODEL.DATE_RANGE.key) {
            return isNameValid && isCalculationFieldsValid &&  this.startDate !== null && this.endDate !== null;
        }
        if (this.condition === POLICY_CONDITION_MODEL.HOUR.key) {
            return isNameValid && isCalculationFieldsValid && this.minValue !== '' && this.maxValue !== '';
        }
        if (this.condition === POLICY_CONDITION_MODEL.BONUS.key) {
            return isNameValid && isCalculationFieldsValid && this.bonusType !== null && this.value !== '';
        }
        return false;
    },
    /**
     * Validates the logical order/range of the scope fields for the rule's condition type.
     * Returns true when fields are empty (not yet filled) to avoid premature errors.
     * @returns {boolean} True when the scope range is logically valid or incomplete.
     */
    isValidScope: function () {
        if (this.condition === POLICY_CONDITION_MODEL.WEEKLY_RECURRENCE.key) {
            if (this.allTime || !this.startTime || !this.endTime) return true;
            // Equal start and end times are allowed for the weekly scope.
            return isEndTimeAfterStartTime(this.startTime, this.endTime, 'HH:mm:ss', true);
        }
        if (this.condition === POLICY_CONDITION_MODEL.DATE_RANGE.key) {
            if (!this.startDate || !this.endDate) return true;
            return new Date(this.endDate).getTime() >= new Date(this.startDate).getTime();
        }
        if (this.condition === POLICY_CONDITION_MODEL.HOUR.key) {
            if (this.minValue === '' || this.maxValue === '') return true;
            return parseFloat(this.maxValue) >= parseFloat(this.minValue);
        }
        if (this.condition === POLICY_CONDITION_MODEL.BONUS.key) {
            if (this.value === '') return true;
            return parseFloat(this.value) <= 100;
        }
        return true;
    },
    changeScope: function (value) {
        return {
            ...this,
            condition: value,
            dayArray: [],
            allTime: false,
            startTime: null,
            endTime: null,
            startDate: null,
            endDate: null,
            minValue: '',
            maxValue: '',
            bonusType: null,
            value: '',
        }
    },
    toRegister: function (rule, index) {
        let payrollPolicyWeeklyScope = null;
        let payrollPolicyDateScope = null;
        let payrollPolicyHourScope = null;
        let payrollPolicyBonusScope = null;

        if( rule.condition === POLICY_CONDITION_MODEL.WEEKLY_RECURRENCE.key){
            payrollPolicyWeeklyScope = [];
            rule.dayArray.forEach((dayId) => {
                payrollPolicyWeeklyScope.push({
                    id: null,
                    dayId,
                    startTime:rule.startTime,
                    endTime:rule.endTime,
                });
            });
        }else if( rule.condition === POLICY_CONDITION_MODEL.DATE_RANGE.key){
            payrollPolicyDateScope = {
                startDate:new Date(rule.startDate).getTime(),
                endDate:new Date(rule.endDate).getTime()
            };
        }else if( rule.condition === POLICY_CONDITION_MODEL.HOUR.key){
            payrollPolicyHourScope = {
                minValue:rule.minValue,
                maxValue:rule.maxValue
            };
        }else if( rule.condition === POLICY_CONDITION_MODEL.BONUS.key){
            payrollPolicyBonusScope = {
                bonusType:rule.bonusType,
                value:rule.value
            };
        }

        return {
            id: rule.id,
            name: rule.name,
            priority: rule.priority ?? null,
            condition: rule.condition,
            calculationType: rule.calculationType,
            calculationValue: rule.calculationValue,
            payrollPolicyWeeklyScope:payrollPolicyWeeklyScope,
            payrollPolicyDateScope:payrollPolicyDateScope,
            payrollPolicyHourScope:payrollPolicyHourScope,
            payrollPolicyBonusScope:payrollPolicyBonusScope
        }
    },
    set: function (data) {
        const weekly      = data.payrollPolicyWeeklyScope ?? [];
        const dateScope   = data.payrollPolicyDateScope   ?? null;
        const hourScope = data.payrollPolicyHourScope ?? null;
        const bonusScope  = data.payrollPolicyBonusScope  ?? null;

        let aux;

        if (data.condition === POLICY_CONDITION_MODEL.WEEKLY_RECURRENCE.key) {
            const startTime = weekly[0]?.startTime ?? null;
            const endTime = weekly[0]?.endTime ?? null;
            // END_TIME is kept for policies saved before "all time" moved to 00:00 - 00:00.
            const allTime = startTime === ALL_TIME_START && (endTime === ALL_TIME_END || endTime === END_TIME);
            aux = {
                dayArray: weekly.map(e => e?.dayId),
                allTime,
                startTime,
                endTime,
            };
        } else if (data.condition === POLICY_CONDITION_MODEL.DATE_RANGE.key) {
            aux = {
                startDate: dateScope?.startDate ?? null,
                endDate: dateScope?.endDate ?? null,
            };
        } else if (data.condition === POLICY_CONDITION_MODEL.HOUR.key) {
            aux = {
                minValue: hourScope?.minValue ?? '',
                maxValue: hourScope?.maxValue ?? '',
            };
        } else {
            aux = {
                bonusType: bonusScope?.bonusType ?? null,
                value:    bonusScope?.value    ?? '',
            };
        }

        return {
            ...this,
            id: data.id,
            name: data.name ?? '',
            priority: data.priority ?? null,
            condition: data.condition ?? null,
            calculationType: data.calculationType ?? null,
            calculationValue: data.calculationValue != null ? String(data.calculationValue) : '',
            statusId: data?.statusId ?? null,
            ...aux
        }
    },
    clear:  function () {
        return {
            ...this,
            id: null,
            name: '',
            condition: null,
            calculationType : null,
            calculationValue: '',
            dayArray: [],
            allTime: false,
            startTime: null,
            endTime: null,
            startDate: null,
            endDate: null,
            fromHours: '',
            toHours: '',
            minValue: '',
            maxValue: '',
            bonusType: null,
            value: '',
        }
    }
};

export const POLICY_MODEL = {
    id: null,
    name: '',
    conflictResolution: null,
    stackMethod: null,
    isGlobal: true,
    departments: [],
    rules: [],
    isValid: function ({ departments }) {
        return this.isValidDetails({ departments }) && this.rules.length > 0;
    },
    /**
     * Validates that the policy details step is complete.
     * By-department policies require at least one department selected.
     * @returns {boolean} True when the policy name is set and department selection is valid.
     */
    isValidDetails: function ({ departments }) {
        return this.name !== '' &&
            (this.isGlobal ||
                (departments?.length > 0 && this.conflictResolution !== null &&
                    (this.conflictResolution !== CONFLICT_RESOLUTION_MODEL.STACKABLE || this.stackMethod !== null)));
    },
    /**
     * Serializes the policy model into the API request body.
     * Fields irrelevant to a rule's condition are set to null to avoid sending stale data.
     * @returns {Object} Payload ready to be sent to the create/update endpoint.
     */
    toRegister: function ({ departments }) {
        return {
            id: this.id,
            name: this.name,
            conflictResolution: this.conflictResolution,
            stackMethod: this.stackMethod,
            departments: this.isGlobal ? null : departments.map(e => ({ departmentId: e.id})),
            rules: this.rules.map((rule, index) => {
                const name =  this.isGlobal ? this.name : rule.name;
                // By-department rules carry a 1-based priority (their order); global has none.
                const priority = this.isGlobal ? null : index + 1;
                return RULE_MODEL.toRegister({...rule, name, priority });
            }),
        };
    },
    toRegisterCloned: function ({ rule= null, departments }) {
        return {
            id: null,
            name: this.isGlobal ? PAYROLL_POLICY_GLOBAL: this.name,
            conflictResolution: this.conflictResolution,
            stackMethod: this.stackMethod,
            departments:  this.isGlobal ? null : departments.map(e => ({ departmentId: e.id})),
            rules: rule
                ? [RULE_MODEL.toRegister({ ...rule, priority: this.isGlobal ? null : 1 })]
                : this.rules.map((e, index) => RULE_MODEL.toRegister({ ...e, priority: this.isGlobal ? null : index + 1 })),
        };
    },
    /**
     * Populates the model from an API response object.
     * Rules are mapped to RULE_MODEL shape so prototype methods remain available.
     * @param {Object} data - Raw policy data from the server.
     * @returns {Object} New policy model instance with server data applied.
     */
    set: function (data) {
        const isGlobal = data.payrollPolicyGroupName === PAYROLL_POLICY_GLOBAL;
        let rules = isGlobal ? [RULE_MODEL.set(data)] : data?.rules.map(e => RULE_MODEL.set(e));

        return {
            ...this,
            id: data.id,
            name: data.name ?? '',
            conflictResolution: data.conflictResolution,
            stackMethod: data.stackMethod,
            isGlobal: isGlobal,
            departments: data.departments?.map(d => d.id) ?? [],
            rules: rules,
        };
    },

    setDepartments: function ({ departments }) {
        return departments?.map(e => ({ id: e?.departmentId, label: e?.departmentName }));
    },
    /**
     * Applies the inherited conflict resolution and stack method from the system config.
     * Existing (user-selected) values take precedence over the inherited defaults.
     * @param {Object} data - System overtime policy group config.
     * @returns {Object} Model with the conflict config applied.
     */
    setConflictConfig: function (data) {
        return {
            ...this,
            conflictResolution: this.conflictResolution ?? data?.conflictResolution ?? null,
            stackMethod: this.stackMethod ?? data?.stackMethod ?? null,
        };
    },
    /**
     * Resets only the details fields (id, name, isGlobal) while keeping the current rules.
     * @param {boolean} isGlobal - Policy scope after clearing.
     * @returns {Object} Model with details reset.
     */
    clearDetails: function (isGlobal) {
        return {
            ...this,
            id: null,
            name: '',
            conflictResolution: null,
            stackMethod: null,
            isGlobal: isGlobal
        };
    },
    /**
     * Resets only the rules array while keeping the current details.
     * Global policies start with one blank rule; by-department policies start with none.
     * @param {boolean} isGlobal - Determines the initial rules state after clearing.
     * @returns {Object} Model with rules reset.
     */
    clearRules: function (isGlobal) {
        return {
            ...this,
            rules: isGlobal ? [RULE_MODEL] : []
        };
    },
};
